<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter } from 'dashboard/composables/store';
import Icon from 'next/icon/Icon.vue';
import { DropdownSection, DropdownItem } from 'next/dropdown-menu/base';

// A lista de contas dos dois seletores: o da sidebar e o do topo do PowerData e das
// PowerApps, que não têm sidebar. Busca e favoritas (Murilo, 24/09: "vamos crescer
// bastante") moram aqui para as duas listas não divergirem.
//
// Nasce a cada abertura (o DropdownContainer só renderiza o conteúdo aberto), então a
// busca começa sempre vazia. Favoritar é só do super admin e fica nas preferências DELE
// no servidor (`ui_settings`), para valer em qualquer computador e nas duas listas.
const props = defineProps({
  tituloDasDemais: { type: String, required: true },
  mostrarPapel: { type: Boolean, default: false },
});

const emit = defineEmits(['trocar']);

const BUSCA_A_PARTIR_DE = 6;

const { t } = useI18n();
const { accountId } = useAccount();
const { isSuperAdmin } = useAdmin();
const { uiSettings, updateUISettings } = useUISettings();
const currentUser = useMapGetter('getCurrentUser');

const busca = ref('');
const campoDeBusca = ref(null);

const contas = computed(() =>
  [...(currentUser.value.accounts || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  )
);

const mostraBusca = computed(() => contas.value.length >= BUSCA_A_PARTIR_DE);
// Sem acento e sem caixa: "saude" acha "Boon Saúde".
const semAcento = texto =>
  (texto || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const idsFavoritos = computed(() =>
  isSuperAdmin.value ? uiSettings.value?.favorite_account_ids || [] : []
);
const ehFavorita = id => idsFavoritos.value.includes(id);

const secoes = computed(() => {
  const termo = semAcento(busca.value.trim());
  const achadas = contas.value.filter(
    conta => !termo || semAcento(conta.name).includes(termo)
  );
  return [
    {
      chave: 'favoritas',
      titulo: t('SIDEBAR_ITEMS.FAVORITE_ACCOUNTS'),
      contas: achadas.filter(c => ehFavorita(c.id)),
    },
    {
      chave: 'demais',
      titulo: props.tituloDasDemais,
      contas: achadas.filter(c => !ehFavorita(c.id)),
    },
  ].filter(secao => secao.contas.length);
});

const alternarFavorita = id => {
  const atual = idsFavoritos.value;
  updateUISettings({
    favorite_account_ids: ehFavorita(id)
      ? atual.filter(favorita => favorita !== id)
      : [...atual, id],
  });
};

// Cursor na busca só com mouse: no celular o foco abre o teclado por cima da lista.
onMounted(() => {
  if (window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) {
    campoDeBusca.value?.focus();
  }
});
</script>

<template>
  <div v-if="mostraBusca" class="px-2 pt-1 pb-2">
    <input
      ref="campoDeBusca"
      v-model="busca"
      type="search"
      class="reset-base w-full h-8 px-3 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 outline-none focus:ring-1 focus:ring-n-brand"
      :placeholder="t('SIDEBAR_ITEMS.SEARCH_ACCOUNTS')"
      :aria-label="t('SIDEBAR_ITEMS.SEARCH_ACCOUNTS')"
    />
  </div>
  <p v-if="!secoes.length" class="px-3 py-2 text-sm text-n-slate-11">
    {{ t('SIDEBAR_ITEMS.NO_ACCOUNTS_FOUND') }}
  </p>
  <!-- Coluna que não cresce com o nome mais longo: sem isso o grid estica a linha para caber o
       nome inteiro, e a estrela sai da caixa (topo no celular). Assim o nome é que encurta. -->
  <DropdownSection
    v-for="secao in secoes"
    :key="secao.chave"
    :title="secao.titulo"
    class="[&>ul]:grid-cols-1"
  >
    <DropdownItem
      v-for="account in secao.contas"
      :id="`account-${account.id}`"
      :key="account.id"
      class="cursor-pointer"
      @click="emit('trocar', account.id)"
    >
      <template #label>
        <div
          :for="account.name"
          class="text-left rtl:text-right flex gap-2 items-center min-w-0"
        >
          <span
            class="text-n-slate-12 truncate min-w-0"
            :class="{ 'max-w-36': mostrarPapel }"
            :title="account.name"
          >
            {{ account.name }}
          </span>
          <template v-if="mostrarPapel">
            <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
            <span
              class="text-n-slate-11 max-w-24 truncate capitalize"
              :title="account.name"
            >
              {{ account.role }}
            </span>
          </template>
        </div>
        <Icon
          v-show="account.id === accountId"
          icon="i-lucide-check"
          class="text-n-teal-11 size-5 flex-shrink-0"
        />
        <!-- A estrela não troca de conta: o clique para aqui. Fica na borda, em coluna. -->
        <button
          v-if="isSuperAdmin"
          type="button"
          class="grid place-content-center size-7 rounded-md hover:bg-n-alpha-2 ms-auto shrink-0"
          :aria-label="
            ehFavorita(account.id)
              ? t('SIDEBAR_ITEMS.FAVORITE_REMOVE')
              : t('SIDEBAR_ITEMS.FAVORITE_ADD')
          "
          :aria-pressed="ehFavorita(account.id)"
          @click.stop.prevent="alternarFavorita(account.id)"
        >
          <span
            class="size-4"
            :class="
              ehFavorita(account.id)
                ? 'i-ri-star-fill text-n-amber-9'
                : 'i-ri-star-line text-n-slate-9'
            "
          />
        </button>
      </template>
    </DropdownItem>
  </DropdownSection>
</template>
